import { useState } from 'react';
import Modal from './Modal.jsx';
import { api } from '../api.js';

const HANDLE_RE = /^[a-z0-9_]{3,20}$/;
const PASSWORD_MIN = 8;

/**
 * A handle and a password, and Google underneath for people who would rather.
 *
 * Everything else about who you are — face, interests, bio — is still edited
 * afterwards in the profile editor rather than demanded here. This asks for
 * the two things it cannot make up for you.
 */
export default function AuthModal({ config, onClose }) {
  const [mode, setMode] = useState('register');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const registering = mode === 'register';
  const ready = HANDLE_RE.test(username) && password.length >= PASSWORD_MIN;

  async function submit(event) {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      if (registering) await api.register(username, password);
      else await api.login(username, password);
      // The same landing Google gets. The socket opened as a guest and the
      // session cookie has just changed underneath it, so the honest thing is
      // to start again rather than patch an identity into a live connection.
      window.location.reload();
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  }

  return (
    <Modal onClose={onClose}>
      <h2>Join the globe</h2>
      <p className="sub">
        Put your face on the map, post in the live chat and connect with people
        you find there.
      </p>

      <div className="tabs" role="tablist">
        <button
          type="button" role="tab" aria-selected={registering}
          className={`tab ${registering ? 'active' : ''}`}
          onClick={() => { setMode('register'); setError(''); }}
        >
          Create account
        </button>
        <button
          type="button" role="tab" aria-selected={!registering}
          className={`tab ${registering ? '' : 'active'}`}
          onClick={() => { setMode('signin'); setError(''); }}
        >
          Sign in
        </button>
      </div>

      {error && <div className="form-error" role="alert">{error}</div>}

      <form onSubmit={submit}>
        <div className="field">
          <label htmlFor="auth-handle">Handle</label>
          <div className="handle-input">
            <span className="handle-prefix" aria-hidden="true">@</span>
            <input
              id="auth-handle"
              value={username}
              maxLength={20}
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              autoComplete="username"
              onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
            />
          </div>
          {registering && (
            <p className="hint">3–20 characters: a–z, 0–9, underscore. This is how people find you.</p>
          )}
        </div>

        <div className="field">
          <label htmlFor="auth-password">Password</label>
          <input
            id="auth-password"
            type="password"
            value={password}
            maxLength={200}
            autoComplete={registering ? 'new-password' : 'current-password'}
            onChange={(e) => setPassword(e.target.value)}
          />
          {registering && <p className="hint">At least {PASSWORD_MIN} characters.</p>}
        </div>

        <button type="submit" className="btn btn-primary btn-block" disabled={!ready || busy}>
          {busy ? 'One moment…' : registering ? 'Create account' : 'Sign in'}
        </button>
      </form>

      {config.google && (
        <>
          <p className="or-rule">or</p>
          <a className="btn btn-google btn-block" href="/api/auth/google">
            <svg viewBox="0 0 18 18" width="17" height="17" aria-hidden="true">
              <path fill="#4285F4" d="M17.6 9.2c0-.6-.1-1.2-.2-1.8H9v3.5h4.8a4.1 4.1 0 0 1-1.8 2.7v2.2h2.9c1.7-1.6 2.7-3.9 2.7-6.6Z" />
              <path fill="#34A853" d="M9 18c2.4 0 4.5-.8 6-2.2l-2.9-2.2c-.8.5-1.8.9-3.1.9-2.4 0-4.4-1.6-5.1-3.8H.9v2.3A9 9 0 0 0 9 18Z" />
              <path fill="#FBBC05" d="M3.9 10.7a5.4 5.4 0 0 1 0-3.4V5H.9a9 9 0 0 0 0 8l3-2.3Z" />
              <path fill="#EA4335" d="M9 3.6c1.3 0 2.5.5 3.4 1.3l2.6-2.6A9 9 0 0 0 .9 5l3 2.3C4.6 5.2 6.6 3.6 9 3.6Z" />
            </svg>
            Continue with Google
          </a>
        </>
      )}

      <p className="hint auth-footnote">
        You can keep browsing without an account — sign in to save your profile
        and make lasting connections.
      </p>
    </Modal>
  );
}
